/**
 * Los botones «llévame ahí» bajo una respuesta del asistente.
 *
 * Llegan por dos carriles y aquí se juntan:
 *  - tarjetas `kind: "action"` (get_app_help y open_in_app): destino EXACTO,
 *    con parámetros («Cambiar Contraseña · Amadou» → su ficha en el paso de
 *    Acceso) o un panel del micrófono («Tarea por voz»);
 *  - `nav`: la página del tema de la guía, el carril antiguo, que sigue
 *    llegando para que una pantalla vieja con un servidor nuevo (o al revés)
 *    no se quede sin botón.
 *
 * Reglas (plan del asistente, F2):
 *  - Como mucho DOS botones y sin duplicados: el concreto gana a la página
 *    genérica de la misma ruta. Con tres ya no se lee ninguno.
 *  - 44 px de alto: se pulsan con el dedo, con guantes, en la nave.
 *  - Navegan por el router (nunca un <a href>, que recargaría la app) y CIERRAN
 *    el panel del chat, que en el teléfono tapa la página a la que se va. Antes
 *    el botón navegaba por detrás del panel y parecía que no hacía nada.
 *  - Toda URL se revalida contra el registro (src/lib/assistantActions.ts)
 *    antes de navegar: aunque la construye el servidor, un destino que no está
 *    en el registro no se sigue.
 */
import { useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, Mic } from "lucide-react";
import { AssistantContext } from "@/contexts/AssistantContext";
import { isAllowedAssistantUrl } from "@/lib/assistantActions";
import { setPendingTaskDraft } from "@/lib/assistantPendingQuestion";
import type { AssistantCardGroup, AssistantNavTarget } from "@/hooks/useAssistantStream";

export interface AssistantActionItem {
  id: string;
  label: string;
  url?: string;
  panel?: "taskVoice" | "scoutingVoice";
  /** Página a la que ir antes de abrir el panel cuando el chat es una página entera. */
  path?: string;
  /** draft_task: el texto de la tarea que el panel de voz interpreta al abrirse. */
  draftText?: string;
  priority?: number;
}

const MAX_BUTTONS = 2;

const pathOf = (url: string) => url.split(/[?#]/)[0];

/**
 * Junta, ordena, filtra y recorta. PURO: lo prueba assistantActionButtons.test.
 * Orden: lo pedido con open_in_app (prioridad 2) → la guía (1) → la página del
 * tema (0). Un botón de página se descarta si ya hay uno concreto a la misma
 * ruta; dos iguales (misma URL o misma etiqueta) cuentan como uno.
 */
export function collectActionButtons(
  cards?: AssistantCardGroup[] | null,
  nav?: AssistantNavTarget[] | null,
): AssistantActionItem[] {
  const fromCards: AssistantActionItem[] = [];
  for (const g of cards ?? []) {
    if ((g.kind as string) !== "action" || !Array.isArray(g.items)) continue;
    for (const raw of g.items) {
      const it = raw as Partial<AssistantActionItem>;
      if (typeof it.label !== "string" || !it.label.trim()) continue;
      if (it.panel === "taskVoice" || it.panel === "scoutingVoice") {
        const draft = (raw as { draft_text?: unknown }).draft_text;
        fromCards.push({
          id: String(it.id ?? it.panel),
          label: it.label,
          panel: it.panel,
          path: typeof it.path === "string" ? it.path : undefined,
          ...(typeof draft === "string" && draft.trim() && it.panel === "taskVoice" ? { draftText: draft } : {}),
          priority: Number(it.priority) || 1,
        });
      } else if (typeof it.url === "string" && isAllowedAssistantUrl(it.url)) {
        fromCards.push({ id: String(it.id ?? it.url), label: it.label, url: it.url, priority: Number(it.priority) || 1 });
      }
    }
  }
  const fromNav: AssistantActionItem[] = (nav ?? [])
    .filter((n) => n && typeof n.route === "string" && typeof n.label === "string" && isAllowedAssistantUrl(n.route))
    .map((n) => ({ id: `nav:${n.route}`, label: n.label, url: n.route, priority: 0 }));

  const all = [...fromCards, ...fromNav].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
  const out: AssistantActionItem[] = [];
  for (const it of all) {
    if (out.length >= MAX_BUTTONS) break;
    const dup = out.some((o) =>
      o.label === it.label ||
      (it.url && o.url === it.url) ||
      (it.panel && o.panel === it.panel) ||
      // La página genérica sobra si ya hay un botón concreto a esa ruta.
      (it.priority === 0 && it.url && o.url && pathOf(o.url) === pathOf(it.url))
    );
    if (!dup) out.push(it);
  }
  return out;
}

export function AssistantActionButtons({ items }: { items: AssistantActionItem[] }) {
  const navigate = useNavigate();
  const location = useLocation();
  /* Opcional a propósito: el chat se monta siempre bajo el proveedor del
     asistente (MainLayout), pero un botón no debe tumbar la burbuja si algún
     día se pinta fuera de él. */
  const assistant = useContext(AssistantContext);

  if (items.length === 0) return null;

  const go = (it: AssistantActionItem) => {
    if (it.panel) {
      if (!assistant) return;
      // La tarea preparada en el chat: el panel la recoge al abrirse y la interpreta.
      if (it.draftText) setPendingTaskDraft(it.draftText);
      /* En escritorio el chat es la página /advanced y ahí el micrófono no se
         monta (AssistantButton): primero se va a la página del flujo. */
      if (location.pathname.startsWith("/advanced") && it.path && isAllowedAssistantUrl(it.path)) navigate(it.path);
      assistant.setActivePanel(it.panel);
      assistant.setIsPanelOpen(true);
      return;
    }
    if (!it.url || !isAllowedAssistantUrl(it.url)) return;
    assistant?.setIsPanelOpen(false);
    navigate(it.url);
  };

  return (
    <div className="mt-2.5 flex flex-wrap gap-2">
      {items.map((it) => {
        const Icon = it.panel ? Mic : ArrowUpRight;
        return (
          <button
            key={`${it.id}:${it.url ?? it.panel}`}
            type="button"
            onClick={() => go(it)}
            data-testid="assistant-action"
            className="inline-flex min-h-[44px] max-w-full items-center gap-1.5 rounded-xl border border-[#2E84DE]/35 bg-[#2E84DE]/5 px-3.5 text-[13px] font-semibold text-[#2E84DE] transition-colors hover:border-[#2E84DE]/60 hover:bg-[#2E84DE]/10 active:scale-[0.98]"
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="truncate">{it.label}</span>
          </button>
        );
      })}
    </div>
  );
}
